import { useMemo, useState, type ReactNode } from "react";
import { Download, LifeBuoy, Share2 } from "lucide-react";

import {
  controlledDocumentIsDownloadable,
  controlledDocumentIsPreviewable,
  downloadControlledDocument,
  filenameExtension,
  type ControlledDocument,
  type DocumentInboundEmailThread,
} from "@/lib/documents";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { hubSupportTicketPagePath, hubSupportTicketPageUrlFull } from "@/lib/navigationTrail";
import { Button } from "@/components/ui/Button";
import {
  hubDocumentPreviewModalBodyClassName,
  hubDocumentPreviewModalOverlayClassName,
  hubDocumentPreviewModalShellClassName,
} from "@/lib/hubLayoutClassNames";
import { HubSupportTicketModal } from "@/components/HubSupportTicketModal";
import { HoverHintTrigger, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { DocumentDetailPreviewPane, type DocumentDetailPreviewMode } from "@/pages/documents/DocumentDetailPreviewPane";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { DocumentSharePanel } from "@/pages/documents/DocumentSharePanel";
import {
  controlledDocumentTargetsAsPills,
  primarySkuFromControlledDocument,
} from "@/pages/documents/controlledDocumentTargets";
import { RelatedEngineeringChangesPanel } from "@/pages/documents/RelatedEngineeringChangesPanel";

function statusClass(status: ControlledDocument["lifecycle_status"]): string {
  if (status === "current") return "bg-green-50 text-green-700";
  if (status === "wip") return "bg-amber-50 text-amber-800";
  if (status === "draft") return "bg-blue-50 text-blue-700";
  return "bg-slate-100 text-slate-600";
}

function documentSupportTicketPrefill(document: ControlledDocument): {
  actual: string;
  expected: string;
} {
  const targets = (document.targets ?? [])
    .map((target) => {
      if (target.target_type === "sku" && target.sku) return `SKU ${target.sku}`;
      if (target.target_type === "collection") {
        return `collection ${target.product_collection_code ?? target.product_collection_id ?? "?"}`;
      }
      return null;
    })
    .filter((value): value is string => value != null);
  const targetLine = targets.length > 0 ? targets.join(", ") : "no targets listed";
  return {
    actual: [
      `Document issue for "${document.title}" (id ${document.id}).`,
      `Kind: ${document.document_kind_label ?? document.document_kind_code ?? "—"}; revision ${document.revision}; lifecycle ${document.lifecycle_status}.`,
      `File: ${document.original_filename ?? "—"}.`,
      `Current targets: ${targetLine}.`,
      "",
      "What is wrong:",
    ].join("\n"),
    expected: "Describe the correct targeting, revision, or file for this document.",
  };
}

export function ControlledDocumentDetailModal(props: {
  document: ControlledDocument;
  onClose: () => void;
  /** Page SKU context (MRP detail tab); falls back to document SKU targets. */
  skuContext?: string | null;
  layout?: "workbench" | "viewer";
  children?: ReactNode;
  downloadDisabled?: boolean;
  documentsReadOnlyTitle?: string;
  inboundEmailThread?: DocumentInboundEmailThread | null;
  previewMode?: DocumentDetailPreviewMode;
  onPreviewModeChange?: (mode: DocumentDetailPreviewMode) => void;
}): JSX.Element {
  const documentsAdmin = useHubModuleAdmin("documents");
  const canPreview = controlledDocumentIsPreviewable(props.document);
  const canDownload = controlledDocumentIsDownloadable(props.document) && !props.downloadDisabled;
  const [shareOpen, setShareOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const layout = props.layout ?? (props.children ? "workbench" : "viewer");
  const bulkShareSku = useMemo(() => {
    const explicit = props.skuContext?.trim();
    if (explicit) {
      return explicit;
    }
    return primarySkuFromControlledDocument(props.document);
  }, [props.document, props.skuContext]);

  const supportPrefill = useMemo(
    () => documentSupportTicketPrefill(props.document),
    [props.document],
  );

  const headerActions = (
    <>
      {canDownload || props.downloadDisabled ? (
      <HoverHintTrigger
        trigger={
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            disabled={!canDownload}
            onClick={(event) => {
              event.stopPropagation();
              void downloadControlledDocument(props.document);
            }}
          >
            <Download className="h-4 w-4 shrink-0" aria-hidden />
            Download
          </button>
        }
      >
        {canDownload
          ? "Download the file for the open document revision."
          : "Download needs Document Control administrator access or a share."}
      </HoverHintTrigger>
      ) : null}
      <HoverHintTrigger
        trigger={
          <button
            type="button"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
            onClick={(event) => {
              event.stopPropagation();
              setSupportOpen(true);
            }}
          >
            <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden />
            Report issue
          </button>
        }
      >
        Open a hub support ticket about this document (wrong target, revision, or file). You can describe the issue before sending.
      </HoverHintTrigger>
      {documentsAdmin ? (
        <HoverHintTrigger
          trigger={
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
              onClick={(event) => {
                event.stopPropagation();
                setShareOpen(true);
              }}
            >
              <Share2 className="h-4 w-4 shrink-0" aria-hidden />
              Share
            </button>
          }
        >
          Share this document, or all documents on the same part number when applicable. Shared people can preview and download even if their Document Control role would not allow it.
        </HoverHintTrigger>
      ) : (
        <ReadOnlyHoverLayer active hoverHint="Document Control administrator access is required to share files.">
          <HoverHintTrigger
            trigger={
              <button
                type="button"
                disabled
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-400 shadow-sm"
              >
                <Share2 className="h-4 w-4 shrink-0" aria-hidden />
                Share
              </button>
            }
          >
            Document Control administrator access is required to share files.
          </HoverHintTrigger>
        </ReadOnlyHoverLayer>
      )}
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {controlledDocumentTargetsAsPills(props.document, {
          linkCollections: true,
          linkSkus: true,
          stopPropagation: true,
        })}
      </div>
    </>
  );

  return (
    <>
      <DocumentModal
        title={props.document.title}
        titleTrailing={headerActions}
        headerExtra={
          <p className="text-sm text-slate-600">
            <span
              className={`mr-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(props.document.lifecycle_status)}`}
            >
              {props.document.lifecycle_status === "wip" ? "WIP" : props.document.lifecycle_status}
            </span>
            <span className="font-medium text-slate-800">{props.document.document_kind_label}</span>
            <span className="text-slate-400"> · Rev {props.document.revision}</span>
            <span className="text-slate-400"> · </span>
            File type{" "}
            <span className="font-medium text-slate-900">
              {filenameExtension(props.document.original_filename) ?? "—"}
            </span>
            <span className="text-slate-400"> · </span>
            <span className="break-all text-slate-600">{props.document.original_filename}</span>
          </p>
        }
        overlayClassName={hubDocumentPreviewModalOverlayClassName()}
        shellClassName={hubDocumentPreviewModalShellClassName()}
        mainClassName={hubDocumentPreviewModalBodyClassName()}
        onClose={props.onClose}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-8">
          {layout === "workbench" && props.children ? (
            <div className="min-h-0 min-w-0 space-y-4 overflow-y-auto lg:max-w-xl lg:flex-shrink-0 xl:max-w-2xl">
              {props.children}
              <RelatedEngineeringChangesPanel
                documentId={props.document.id}
                detailPathPrefix="/documents/change-orders"
              />
            </div>
          ) : null}
          {canPreview ? (
          <aside className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <DocumentDetailPreviewPane
              document={props.document}
              inboundEmailThread={props.inboundEmailThread}
              fillAvailableHeight
              previewMode={props.previewMode}
              onPreviewModeChange={props.onPreviewModeChange}
            />
          </aside>
          ) : (
          <aside className="flex min-h-0 min-w-0 flex-1 flex-col justify-center rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-10">
            <p className="text-center text-sm text-slate-600">
              Preview needs Document Control read-write or administrator access, or a share.
            </p>
          </aside>
          )}
        </div>
      </DocumentModal>

      {shareOpen ? (
        <DocumentModal
          title={`Share — ${props.document.title}`}
          onClose={() => setShareOpen(false)}
          shellClassName="max-w-lg"
          overlayClassName="z-[60] px-4 py-8"
        >
          <DocumentSharePanel
            compact
            documentRecordId={props.document.id}
            sku={bulkShareSku}
            allowSkuBulkScope={bulkShareSku != null}
          />
          <div className="mt-4 flex justify-end">
            <Button type="button" variant="secondary" onClick={() => setShareOpen(false)}>
              Close
            </Button>
          </div>
        </DocumentModal>
      ) : null}

      {supportOpen ? (
        <HubSupportTicketModal
          onClose={() => setSupportOpen(false)}
          title="Report document issue"
          description="Describe what is wrong with this document (target, revision, or file). We will use the ticket to fix leftovers after bulk retargets."
          initialKind="bug"
          initialActualBehavior={supportPrefill.actual}
          initialExpectedBehavior={supportPrefill.expected}
          pagePathOverride={(() => {
            const params = new URLSearchParams(window.location.search);
            params.set("document", props.document.id);
            const search = params.toString();
            return hubSupportTicketPagePath(
              window.location.pathname,
              search === "" ? "" : `?${search}`,
            );
          })()}
          pageUrlFullOverride={hubSupportTicketPageUrlFull(window.location)}
        />
      ) : null}
    </>
  );
}
